import React from 'react';
import { useProperties } from '../../../hooks/useProperties';
import { formatPrice } from '../../../utils/formatters';
import { FinancialChart } from './charts/FinancialChart';

interface FinancialStatsProps {
  dateRange: [Date, Date];
  selectedAgents: string[];
  selectedTypes: string[];
}

export const FinancialStats: React.FC<FinancialStatsProps> = ({
  dateRange,
  selectedAgents,
  selectedTypes,
}) => {
  const { properties } = useProperties();

  // Filtrer les propriétés selon les critères
  const filteredProperties = properties.filter(property => {
    const inDateRange = property.createdAt >= dateRange[0] && property.createdAt <= dateRange[1];
    const matchesAgent = selectedAgents.length === 0 || selectedAgents.includes(property.agent);
    const matchesType = selectedTypes.length === 0 || selectedTypes.includes(property.type);
    return inDateRange && matchesAgent && matchesType;
  });

  // Calculer les totaux
  const soldProperties = filteredProperties.filter(p => p.status === 'sold');
  const rentedProperties = filteredProperties.filter(p => p.status === 'rented');

  const totalSales = soldProperties.reduce((acc, p) => acc + p.price, 0);
  const totalRent = rentedProperties.reduce((acc, p) => acc + p.price, 0);
  const totalCommissions = soldProperties.reduce((acc, p) => acc + p.commission, 0) +
    rentedProperties.reduce((acc, p) => acc + p.commission, 0);

  return (
    <section className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
        Indicateurs financiers
      </h2>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Volume des ventes
          </div>
          <div className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
            {Math.floor(totalSales).toLocaleString('fr-FR').replace(/,/g, ' ')} DA
          </div>
          <div className="mt-1 text-sm text-green-500">
            +12% par rapport à la période précédente
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Volume des locations
          </div>
          <div className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
            {Math.floor(totalRent).toLocaleString('fr-FR').replace(/,/g, ' ')} DA
          </div>
          <div className="mt-1 text-sm text-green-500">
            +8% par rapport à la période précédente
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Total des commissions
          </div>
          <div className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
            {Math.floor(totalCommissions).toLocaleString('fr-FR').replace(/,/g, ' ')} DA
          </div>
          <div className="mt-1 text-sm text-green-500">
            +15% par rapport à la période précédente
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <FinancialChart properties={filteredProperties} />
      </div>
    </section>
  );
};